/**
 * TrendCompare 共享工具
 *
 * 设计原则：
 * - ECharts series 的 `name` 直接用 `game_id`（保证 tooltip/legend params 稳定可反查）
 * - 通过 `legend.formatter` / `listTitleFunc` 把 game_id 翻译为 game_name + icon
 * - rich 标记 key 用 `gameRichKey(game_id)` 转义
 * - 颜色通过 `gameColorMap`（Record<gameId, color>）统一控制，确保跨图表的一致性
 */
import { DefaultImg } from '@op/constants/defaultImg';
import { DBO_V } from '@op/utils/format';
import { getPercentageInfo } from '@op/utils';
import { ITrendSeries } from '../../types/trend';
import { TGameMap } from '../../hooks/useGameMap';
import type { TGameColorMap } from '../../hooks/useGameColorMap';

/** 把 game_id 转为安全的 rich 标记 key（仅字母数字下划线） */
export function gameRichKey(gameId: string): string {
  return `g_${(gameId || '').replace(/[^a-zA-Z0-9_]/g, '_')}`;
}

/**
 * 构造 ECharts legend / axisLabel 用的 rich 字段
 * 每个 game_id 都有对应 cover 图片的 rich 配置
 */
export function buildGameRich(
  series: ITrendSeries[],
  gameMap: TGameMap,
  size = 16,
): Record<string, any> {
  const rich: Record<string, any> = {
    name: {
      color: '#888888',
      fontSize: 12,
      verticalAlign: 'middle',
      padding: [2, 0, 0, 4],
    },
  };
  series.forEach(s => {
    const game = gameMap[s.game_id];
    const img = game?.cover || DefaultImg.GAME;
    rich[gameRichKey(s.game_id)] = {
      width: size,
      height: size,
      backgroundColor: { image: img },
      verticalAlign: 'middle',
      borderRadius: 4,
    };
  });
  return rich;
}

/**
 * 构造每个游戏的 ECharts series 项
 *
 * ⚠️ 关键：series.name = game_id（不是 game_name）。
 * - legend.formatter / tooltip.formatter 拿到的 seriesName 都是 game_id
 * - 由 formatter 自行翻译为 game_name + icon
 * - 通过 gameColorMap 统一设置每个游戏的图例颜色，确保跨图表一致
 */
export function buildEchartSeries(
  series: ITrendSeries[],
  type: 'line' | 'bar',
  gameColorMap?: TGameColorMap,
) {
  return series.map(s => {
    const color = gameColorMap?.[s.game_id];
    return {
      name: s.game_id,
      type,
      data: s.values.map(v => v.value),
      // 全局统一颜色：设置 itemStyle.color 覆盖 ECharts 默认色板循环
      ...(color ? { itemStyle: { color } } : {}),
    };
  });
}

/** 取第一个序列的 dates 作为 X 轴（mock 保证所有游戏 dates 对齐） */
export function buildXAxisData(series: ITrendSeries[]): string[] {
  return series[0]?.values.map(v => v.date) ?? [];
}

/**
 * legend.formatter / axisLabel.formatter 共用：
 * 把 game_id 渲染为「[icon] game_name」 rich 文本
 */
export function gameRichLabel(gameId: string, gameMap: TGameMap): string {
  const game = gameMap[gameId];
  const name = game?.game_name || gameId;
  return `{${gameRichKey(gameId)}|}{name|${name}}`;
}

/**
 * tooltip 中单游戏行：游戏 icon + 名称（HTML 字符串）
 */
export function gameTooltipLabel(gameId: string, gameMap: TGameMap): string {
  const game = gameMap[gameId];
  const name = game?.game_name || gameId;
  const cover = game?.cover || DefaultImg.GAME;
  return `<span class="dbo-chart-formatter-img"><img src="${cover}" class="chart-common-formatter-img" style="background-image: url(${DefaultImg.GAME}); background-size: cover; background-position: center;" />${name}</span>`;
}

/**
 * 构造 Pie label.formatter
 *
 * 输出格式（参考 opinion LeftChart Chart.tsx L117-177）：
 * ```
 * [icon] {game_name}: {value}({percent})
 * ─────────────
 * ```
 */
/**
 * Pie series.label 配置（严格对齐 LeftChart Chart.tsx L117-164）
 *
 * formatter 输出：
 *   `[icon]{value| {name}}: {a|{value}({percent})}\n{hrLine|}\n{a|}`
 *
 * 注意：
 * - `value` / `a` 是 rich 标记名（不是真实数据字段名），不要在 rich 中加额外样式
 * - `hrLine` 必须 borderColor: 'auto' + height: 0.5，让横线跟随扇区颜色
 * - 末尾的 `\n{a|}` 是占位行，让延长线与 hr 对齐
 */
export function pieGameLabel(gameMap: TGameMap, total: number, size = 16) {
  return {
    color: '#262832',
    alignTo: 'edge' as const,
    edgeDistance: 10,
    formatter: (params: any) => {
      const gameId: string = params?.name;
      const game = gameMap[gameId];
      const name = game?.game_name || gameId;
      const icon = `{${gameRichKey(gameId)}|}`;
      const value = DBO_V.reviews(params?.value);
      const percent = total
        ? getPercentageInfo(params?.value, total, 2)
        : `${(params?.percent ?? 0).toFixed(2)}%`;
      const valueText = params?.value ? `${value}(${percent})` : value;
      return `${icon}{value| ${name}}: {a|${valueText}}\n{hrLine|}\n{a|}`;
    },
    rich: {
      a: {
        // color: '#999',
        lineHeight: 20, // 设置最后一行空数据高度，为了能让延长线与hr线对接起来
        align: 'center',
      },
      hrLine: {
        // 设置hr是为了让中间线能够自适应长度
        borderColor: 'auto', // hr的颜色为auto时候会主动显示颜色的
        width: '105%',
        borderWidth: 0.5,
        height: 0.5,
      },
      per: {
        // 用百分比数据来调整下数字位置，显的好看些。如果不设置，formatter最后一行的空数据就不需要
        padding: [4, 0],
      },
      // 每个 game_id 对应一个 image rich
      ...(() => {
        const rich: Record<string, any> = {};
        Object.keys(gameMap).forEach(gameId => {
          const game = gameMap[gameId];
          const img = game?.cover || DefaultImg.GAME;
          rich[gameRichKey(gameId)] = {
            width: size,
            height: size,
            backgroundColor: { image: img },
            verticalAlign: 'middle' as const,
            borderRadius: 4,
          };
        });
        return rich;
      })(),
    },
  };
}
